// Prime Video artwork role derived from the catalog classification, and a size
// check against Prime Video's linear artwork spec. The role derivation MUST match
// toArtworkRole in nosey-mrss-feed (services/youtube/amazon-epg.ts): the BE is
// what actually publishes the role in the EPG; this only shows it to operators.

export type AmazonArtworkRole = "cover" | "poster" | "background";

export const amazonArtworkRole = (
	kind?: string | null,
	titled?: string | null
): AmazonArtworkRole | undefined => {
	const k = (kind ?? "").trim().toLowerCase();
	const t = (titled ?? "").trim().toLowerCase();
	if (k === "landscape poster" && t === "titled") return "cover";
	if (k === "landscape poster" && t === "untitled") return "background";
	if (k === "portrait poster" && t === "titled") return "poster";
	if (k === "thumbnail" && t === "untitled") return "background";
	return undefined;
};

interface ArtworkSpec {
	aspect: [number, number];
	preferred: [number, number];
	minimum: [number, number];
	label: string;
}

export const AMAZON_ARTWORK_SPECS: Record<AmazonArtworkRole, ArtworkSpec> = {
	cover: { aspect: [16, 9], preferred: [3840, 2160], minimum: [1920, 1080], label: "16:9 with title" },
	background: { aspect: [16, 9], preferred: [3840, 2160], minimum: [1920, 1080], label: "16:9, no title" },
	poster: { aspect: [2, 3], preferred: [2000, 3000], minimum: [2000, 3000], label: "2:3 with title" },
};

export type AmazonSizeCheck = "preferred" | "minimum" | "too-small" | "wrong-aspect" | "unknown";

/** How an image's dimensions measure up to its role's spec (1% aspect tolerance). */
export const amazonSizeCheck = (
	role: AmazonArtworkRole,
	width?: number | string | null,
	height?: number | string | null
): AmazonSizeCheck => {
	const w = Number(width);
	const h = Number(height);
	if (!w || !h) return "unknown";
	const spec = AMAZON_ARTWORK_SPECS[role];
	const expected = spec.aspect[0] / spec.aspect[1];
	if (Math.abs(w / h - expected) / expected > 0.01) return "wrong-aspect";
	if (w >= spec.preferred[0] && h >= spec.preferred[1]) return "preferred";
	if (w >= spec.minimum[0] && h >= spec.minimum[1]) return "minimum";
	return "too-small";
};

export const AMAZON_SIZE_CHECK_TEXT: Record<AmazonSizeCheck, string> = {
	preferred: "meets Prime Video's preferred size",
	minimum: "meets the minimum; the preferred size is larger",
	"too-small": "below Prime Video's minimum size",
	"wrong-aspect": "wrong aspect ratio for this role",
	unknown: "dimensions unknown",
};

export const describeAmazonRole = (role: AmazonArtworkRole): string => {
	const spec = AMAZON_ARTWORK_SPECS[role];
	return `${role} (${spec.label}, ${spec.preferred[0]}x${spec.preferred[1]})`;
};
